import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  useRouteMatch,
  Route,
  Switch,
  useLocation,
  Link,
  useHistory,
} from "react-router-dom";

import qs from "query-string";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import useDebounce from "react-use/lib/useDebounce";

import Button from "react-bootstrap/Button";
import Select from "react-select";
import DatePicker from "react-datepicker";

import {
  QUERY_CLIENTS_FOR_SIDEBAR_FOR_ADMIN,
  QUERY_USER_ACTIVITY_LOGS,
} from "../../config/graphql/query";

import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

import { reactSelectCustomStyles } from "../Employees/Employee/Information";
import UserActivityLog from "./UserActivityLog";

const UserActivityLogActions = ["login", "logout"] as const;
const UserActivityLogStatuses = ["success", "failure"] as const;

const limit = 10;

const TableRow = ({ item }: { item: IUserActivityLog }) => {
  const { id, user, action, status, createdAt, isTwoFactorAuth, metadata } =
    item;

  const { t } = useTranslation(["errorLogs", "common"]);

  const roles =
    user?.roles.map((role) =>
      role === "CLIENT_ADMIN" &&
      ["distributor", "reseller"].includes(user.client.type)
        ? user.client.type.toUpperCase()
        : role,
    ) ?? [];

  return (
    <>
      <tr>
        <td>{`${user?.firstName || ""} ${user?.lastName || ""}`}</td>
        <td>{user?.email || metadata?.email || ""}</td>
        <td>{roles.join(" , ")}</td>
        <td>{user?.client.title || ""}</td>
        <td>{action.toUpperCase()}</td>
        <td>{isTwoFactorAuth?.toString().toUpperCase()}</td>
        <td>{status.toUpperCase()}</td>
        <td>{dayjs(createdAt).local().format("DD-MM-YYYY / HH:mm")}</td>
        <td className="text-nowrap">
          <div className="d-flex">
            <Link to={`userActivityLogs/${id}`}>
              <Button size="sm" variant="primary">
                {t("common:view")}
              </Button>
            </Link>
          </div>
        </td>
      </tr>
    </>
  );
};

const UserActivityLogs = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation(["userActivity", "common"]);

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        page?: number;
        search?: string;
        startDate?: Date;
        endDate?: Date;
        action?: UserActivityLogAction;
        status?: UserActivityLogStatus;
        clientId?: string;
      },
    [location.search],
  );
  const [search, setSearch] = useState(query.search);

  const [startDate, setStartDate] = useState(
    query.startDate ? dayjs(query.startDate).toDate() : undefined,
  );
  const [endDate, setEndDate] = useState(
    query.endDate ? dayjs(query.endDate).toDate() : undefined,
  );

  const [action, setAction] = useState<UserActivityLogAction | undefined>(
    query.action,
  );

  const [status, setStatus] = useState<UserActivityLogStatus | undefined>(
    query.status,
  );

  const [clientId, setClientId] = useState<string | undefined>(query.clientId);

  const page = useMemo(() => Math.max(query.page || 0, 1), [query.page]);

  const variables = useMemo(() => {
    const nextVariables = {
      pagination: {
        limit,
        skip: (page - 1) * limit,
      },
      ...(startDate || endDate || search || action || status || clientId
        ? {
            filter: {
              ...((startDate || endDate) && {
                createdAt: {
                  ...(startDate && {
                    GTE: dayjs(startDate).hour(0).minute(0).second(0).format(),
                  }),
                  ...(endDate && {
                    LTE: dayjs(endDate).hour(23).minute(59).second(59).format(),
                  }),
                },
              }),
              ...(action && { action }),
              ...(status && { status }),
              ...(search && { search: search?.toString() }),
              ...(clientId && { client: clientId }),
            },
          }
        : {}),
    };

    return nextVariables;
  }, [page, startDate, endDate, search, action, status, clientId]);

  const { data } = useQuery<{
    userActivityLogs?: {
      logs: [IUserActivityLog];
      logsCount: number;
    };
  }>(QUERY_USER_ACTIVITY_LOGS, {
    fetchPolicy: "network-only",
    variables,
  });

  const userActivityLogs = data?.userActivityLogs?.logs ?? [];
  const userActivityLogsCount = data?.userActivityLogs?.logsCount ?? 0;

  const userActivityLogActionOptions = UserActivityLogActions.map((type) => ({
    value: type,
    label: type.toUpperCase(),
  }));

  const userActivityLogStatusOptions = UserActivityLogStatuses.map((type) => ({
    value: type,
    label: type.toUpperCase(),
  }));

  const { data: clientsData } = useQuery<{ clients: IClient[] }>(
    QUERY_CLIENTS_FOR_SIDEBAR_FOR_ADMIN,
    { fetchPolicy: "cache-first" },
  );

  const clientOptions =
    clientsData?.clients.map((c) => ({
      value: c.id,
      label: c.title,
    })) ?? [];

  const renderLog = useCallback(
    (item: IUserActivityLog) => <TableRow key={item.id} item={item} />,
    [],
  );

  useDebounce(
    () => {
      history.push({
        search: qs.stringify({
          page: 1,
          ...(search && { search }),
          ...(startDate && {
            startDate: dayjs(startDate).format("YYYY-MM-DD"),
          }),
          ...(endDate && {
            endDate: dayjs(endDate).format("YYYY-MM-DD"),
          }),
          ...(action && { action }),
          ...(status && { status }),
          ...(clientId && { clientId }),
        }),
      });
    },
    500,
    [search, startDate, endDate, action, status, clientId],
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("userActivity:nav.userActivityLogs")}
          </li>
        </ol>
      </nav>
      <form className="form-inline justify-content-between">
        <div className="mr-5 mb-4">
          <label htmlFor="search" className="sr-only">
            {t("common:search")}
          </label>
          <input
            id="search"
            type="search"
            className="form-control"
            placeholder={t("common:search")}
            value={search}
            onChange={({ target: { value } }) => {
              setSearch(value);
            }}
          />
        </div>
        <div className="d-flex mb-4 mr-0 mr-sm-5">
          <div className="d-flex mr-4">
            <div className="mr-2 d-flex align-items-center">
              {t("userActivity:logs.date.from")}
            </div>
            <DatePicker
              isClearable
              selected={startDate}
              onChange={(date: Date) => {
                setStartDate(date);
                if (
                  date.toDateString() === new Date().toDateString() ||
                  dayjs(date).isAfter(dayjs(endDate))
                ) {
                  setEndDate(date);
                }
              }}
              filterDate={(date) => dayjs() > dayjs(date)}
              placeholderText="Start Date"
              dateFormat="MM/dd/yyyy"
              className="form-control"
              todayButton="Today"
            />
          </div>
          <div className="d-flex">
            <div className="mr-2 d-flex align-items-center">
              {t("userActivity:logs.date.to")}
            </div>
            <DatePicker
              isClearable
              selected={endDate}
              onChange={(date: Date) => {
                setEndDate(date);
              }}
              filterDate={(date) => {
                if (!startDate) {
                  return dayjs() > dayjs(date);
                }
                return dayjs() > dayjs(date) && dayjs(date) >= dayjs(startDate);
              }}
              placeholderText="End Date"
              dateFormat="MM/dd/yyyy"
              className="form-control"
            />
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row">
          <div className="d-flex mb-4 mr-3">
            <label htmlFor="client" className="mb-0 mr-2 align-self-center">
              {t("userActivity:logs.th.client")}
            </label>
            <div style={{ width: "13rem" }}>
              <Select
                isClearable
                options={clientOptions}
                value={clientOptions.find((opt) => opt.value === clientId)}
                onChange={(value) => setClientId(value?.value)}
                styles={reactSelectCustomStyles(false)}
              />
            </div>
          </div>
          <div className="d-flex mb-4 mr-3">
            <label htmlFor="action" className="mb-0 mr-2 align-self-center">
              {t("userActivity:logs.action.label")}
            </label>
            <div style={{ width: "13rem" }}>
              <Select
                isClearable
                options={userActivityLogActionOptions}
                value={userActivityLogActionOptions.find(
                  (option) => option.value === action,
                )}
                onChange={(value) => {
                  setAction(value?.value);
                }}
                styles={reactSelectCustomStyles(false)}
              />
            </div>
          </div>
          <div className="d-flex mb-4">
            <label htmlFor="status" className="mb-0 mr-2 align-self-center">
              {t("userActivity:logs.status.label")}
            </label>
            <div style={{ width: "13rem" }}>
              <Select
                isClearable
                options={userActivityLogStatusOptions}
                value={userActivityLogStatusOptions.find(
                  (option) => option.value === status,
                )}
                onChange={(value) => {
                  setStatus(value?.value);
                }}
                styles={reactSelectCustomStyles(false)}
              />
            </div>
          </div>
        </div>
      </form>
      <Table>
        <thead>
          <tr>
            <th scope="col">{t("userActivity:logs.th.fullName")}</th>
            <th scope="col">{t("userActivity:logs.th.email")}</th>
            <th scope="col">{t("userActivity:logs.th.roles")}</th>
            <th scope="col">{t("userActivity:logs.th.client")}</th>
            <th scope="col">{t("userActivity:logs.th.action")}</th>
            <th scope="col">{t("userActivity:logs.th.2fa")}</th>
            <th scope="col">{t("userActivity:logs.th.status")}</th>
            <th scope="col">{t("userActivity:logs.th.createdAt")}</th>
            <th scope="col">{t("userActivity:logs.th.actions")}</th>
          </tr>
        </thead>
        <tbody>{userActivityLogs.map(renderLog)}</tbody>
      </Table>
      <Pagination documentsCount={userActivityLogsCount} limit={limit} />
    </div>
  );
});

const UserActivityLogsRoute = React.memo(() => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <UserActivityLogs />
      </Route>
      <Route path={`${path}/:id`}>
        <UserActivityLog />
      </Route>
    </Switch>
  );
});

export default UserActivityLogsRoute;
